// src/modules/wallet/WalletApi.ts
import apiClient from '@skygroup/shared/API/apiClient';
import endpoints from '@skygroup/shared/API/endpoints';
import type {
    SendOtpRequest,
    SendOtpResponse,
    VerifyOtpRequest,
    VerifyOtpResponse,
    GetProfileResponse,
    LogoutResponse,
    UpdateProfileRequest,
    UpdateProfileResponse,
    StartPhoneChangeRequest,
    StartPhoneChangeResponse,
    VerifyPhoneChangeRequest,
    VerifyPhoneChangeResponse,
    RegisterRequest,
    RegisterResponse,
} from './membershipInterface';

/**
 * Register a new member
 * @param data - Phone, first name, last name, optional email
 * @returns Promise with registration result
 */
export const register = async (data: RegisterRequest): Promise<RegisterResponse> => {
    try {
        const response = await apiClient.post<RegisterResponse>(
            endpoints.AUTH.REGISTER_MEMBERSHIP,
            {
                phone: data.phone,
                first_name: data.first_name,
                last_name: data.last_name,
                email: data.email,
                newsletter: data.newsletter,
            },
            { withCredentials: true }
        );

        if (response.data.status !== 'success') {
            throw new Error(response.data.message);
        }

        return response.data;
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message ||
            error?.response?.data?.errors ||
            error?.message;
        throw new Error(Array.isArray(errorMessage) ? errorMessage.join(', ') : errorMessage);
    }
};

export interface UpdateMemberRequest {
    secret: string;
    phone: string;
    first_name: string;
    last_name: string;
    email?: string;
    newsletter?: boolean;
}

/**
 * Update member profile (no phone change).
 * @param data - first_name, last_name, optional email and newsletter
 * @returns Promise with updated membership user data
 */
export const updateMember = async (data: UpdateMemberRequest) => {
    try {
        const response = await apiClient.post(
            endpoints.AUTH.UPDATE_MEMBERSHIP,
            {
                secret: data.secret,
                phone: data.phone,
                first_name: data.first_name,
                last_name: data.last_name,
                email: data.email,
                newsletter: data.newsletter,
            },
            { withCredentials: true }
        );

        if (response.data?.status !== 'success') {
            throw new Error(response.data?.message);
        }

        return response.data;
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message ||
            error?.response?.data?.errors ||
            error?.message;
        throw new Error(Array.isArray(errorMessage) ? errorMessage.join(', ') : errorMessage);
    }
};